export function dropSide(keys, from, to) {
  return keys.indexOf(to) > keys.indexOf(from) ? "after" : "before";
}

export function reorderKeys(keys, from, to) {
  const fromIndex = keys.indexOf(from);
  const toIndex = keys.indexOf(to);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return null;
  const next = keys.slice();
  next.splice(fromIndex, 1);
  next.splice(toIndex, 0, from);
  return next;
}

export function moveKey(keys, key, direction) {
  const index = keys.indexOf(key);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= keys.length) return null;
  const next = keys.slice();
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function mergeOrder(full, visible) {
  if (!full || full.length === 0) return visible;
  const known = new Set(full);
  const shown = visible.filter((key) => known.has(key));
  const showing = new Set(shown);
  let next = 0;
  const merged = full.map((key) => (showing.has(key) ? shown[next++] : key));
  return merged.concat(visible.filter((key) => !known.has(key)));
}

export function placeLanes(container, keys) {
  const lanes = Array.from(container.children).filter((child) => child.dataset && child.dataset.laneKey);
  if (lanes.length === 0) return false;
  const byKey = new Map(lanes.map((lane) => [lane.dataset.laneKey, lane]));
  const anchor = lanes[lanes.length - 1].nextSibling;
  keys.forEach((key) => {
    const lane = byKey.get(key);
    if (lane) container.insertBefore(lane, anchor);
  });
  return true;
}

export function markCustomOrder(root) {
  root.querySelectorAll(".tg-filters").forEach((filters) => {
    const reset = filters.querySelector(".tg-filters-reset");
    if (!reset || !reset.hidden) return;
    reset.hidden = false;
    const count = filters.querySelector(".tg-filters-count");
    if (count) {
      const active = Number(count.dataset.count || 0) + 1;
      count.dataset.count = String(active);
      count.textContent = String(active);
      count.setAttribute("aria-label", `${active} active`);
      count.hidden = false;
    }
    const button = filters.querySelector(".tg-filters-button");
    if (button) button.classList.add("tg-filters-active");
  });
}

export function reportLaneOrderError(error, step) {
  if (typeof console !== "undefined") console.warn(`Lane order ${step} failed`, error);
  if (typeof window !== "undefined" && window.HoneyBadger) window.HoneyBadger.report({ err: error, source: "training-grooming lane order", step });
}

export function disposeAll(savers) {
  Object.values(savers || {}).forEach((saver) => saver.dispose());
  return {};
}

export function createOrderSaver({ confirmed, place, send, notify, failed, onSaved = () => {}, report = reportLaneOrderError }) {
  let accepted = confirmed;
  let pending = null;
  let running = null;
  let disposed = false;

  async function attempt(order) {
    try {
      return await send(order);
    } catch (error) {
      report(error, "save");
      return false;
    }
  }

  function quietly(callback, step) {
    try {
      callback();
    } catch (error) {
      report(error, step);
      return false;
    }
    return true;
  }

  async function drain(order) {
    let next = order;
    let saved = false;
    try {
      while (next && !disposed) {
        const sending = next;
        pending = null;
        saved = await attempt(sending);
        if (disposed) return false;
        if (saved) {
          accepted = sending;
          quietly(() => onSaved(sending), "onSaved");
        }
        next = pending;
      }
      if (!saved && !disposed) {
        quietly(() => place(accepted), "revert");
        quietly(() => notify.error(failed), "notify");
      }
      return saved && !disposed;
    } finally {
      running = null;
    }
  }

  return {
    move(order) {
      if (disposed) return Promise.resolve(false);
      quietly(() => place(order), "place");
      if (running) {
        pending = order;
        return running;
      }
      running = drain(order);
      return running;
    },
    dispose() {
      disposed = true;
      pending = null;
    },
    confirmed: () => accepted,
  };
}

export function focusLaneMenu(root, key) {
  const button = root.querySelector(`.tg-lane-head[data-lane-key="${key}"] .tg-lane-menu-btn`);
  if (button) button.focus({ preventScroll: true });
  return !!button;
}
